import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { CensusState, CensusFile, CensusFileMetadata, PlanSummary, CensusStatistics, CensusUploadRequest } from '../../types/census.types';
import { tokenManager } from '../../utils/tokenManager';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/dev/api/v1';

const initialState: CensusState = {
    files: [],
    selectedFile: null,
    planSummaries: [],
    statistics: null,
    loading: false,
    uploading: false,
    downloading: false,
    error: null,
    uploadProgress: 0,
    filters: {}
};

// Async thunks
export const uploadCensusFile = createAsyncThunk(
    'census/uploadFile',
    async (uploadData: CensusUploadRequest, { rejectWithValue }) => {
        try {
            const token = await tokenManager.getToken();
            const authType = tokenManager.getAuthType();
            
            const formData = new FormData();
            formData.append('file', uploadData.file);
            formData.append('employer_name', uploadData.employer_name);
            formData.append('plan_code', uploadData.plan_code);
            if (uploadData.plan_name) {
                formData.append('plan_name', uploadData.plan_name);
            }
            if (uploadData.plan_owner_carrier) {
                formData.append('plan_owner_carrier', uploadData.plan_owner_carrier);
            }
            if (uploadData.uploaded_by) {
                formData.append('uploaded_by', uploadData.uploaded_by);
            }
            if (uploadData.force_upload) {
                formData.append('force_upload', 'true');
            }

            const response = await fetch(`${API_BASE_URL}/census/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Auth-Type': authType || 'aws'
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Upload failed');
            }

            return await response.json();
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchCensusFiles = createAsyncThunk(
    'census/fetchFiles',
    async (filters: { employer_name?: string; plan_code?: string; status?: string; limit?: number } = {}, { rejectWithValue }) => {
        try {
            const token = await tokenManager.getToken();
            const authType = tokenManager.getAuthType();
            
            const queryParams = new URLSearchParams();
            if (filters.employer_name) queryParams.append('employer_name', filters.employer_name);
            if (filters.plan_code) queryParams.append('plan_code', filters.plan_code);
            if (filters.status) queryParams.append('status', filters.status);
            if (filters.limit) queryParams.append('limit', filters.limit.toString());

            const response = await fetch(`${API_BASE_URL}/census/files?${queryParams}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Auth-Type': authType || 'aws'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch census files');
            }

            return await response.json();
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchFileMetadata = createAsyncThunk(
    'census/fetchFileMetadata',
    async (fileId: number, { rejectWithValue }) => {
        try {
            const token = await tokenManager.getToken();
            const authType = tokenManager.getAuthType();

            const response = await fetch(`${API_BASE_URL}/census/files/${fileId}/metadata`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Auth-Type': authType || 'aws'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch file metadata');
            }

            return await response.json();
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const downloadCensusFile = createAsyncThunk(
    'census/downloadFile',
    async (fileId: number, { rejectWithValue }) => {
        try {
            const token = await tokenManager.getToken();
            const authType = tokenManager.getAuthType();

            const response = await fetch(`${API_BASE_URL}/census/files/${fileId}/download`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Auth-Type': authType || 'aws'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to download file');
            }

            // Get filename from response headers
            const contentDisposition = response.headers.get('Content-Disposition');
            const filename = contentDisposition ? 
                contentDisposition.split('filename=')[1]?.replace(/"/g, '') : 
                'download';

            const blob = await response.blob();
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            return { success: true, filename };
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const deleteCensusFile = createAsyncThunk(
    'census/deleteFile',
    async (fileId: number, { rejectWithValue }) => {
        try {
            const token = await tokenManager.getToken();
            const authType = tokenManager.getAuthType();

            const response = await fetch(`${API_BASE_URL}/census/files/${fileId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Auth-Type': authType || 'aws'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete file');
            }

            return { fileId };
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchPlanSummaries = createAsyncThunk(
    'census/fetchPlanSummaries',
    async (limit: number = 50, { rejectWithValue }) => {
        try {
            const token = await tokenManager.getToken();
            const authType = tokenManager.getAuthType();

            const response = await fetch(`${API_BASE_URL}/census/plan-summaries?limit=${limit}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Auth-Type': authType || 'aws'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch plan summaries');
            }

            return await response.json();
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchCensusStatistics = createAsyncThunk<any, void>(
    'census/fetchStatistics',
    async (_, { rejectWithValue }) => {
        try {
            const token = await tokenManager.getToken();
            const authType = tokenManager.getAuthType();

            const response = await fetch(`${API_BASE_URL}/census/statistics`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Auth-Type': authType || 'aws'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch statistics');
            }

            return await response.json();
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

const censusSlice = createSlice({
    name: 'census',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setFilters: (state, action: PayloadAction<{ employer_name?: string; plan_code?: string; status?: string }>) => {
            state.filters = action.payload;
        },
        clearFilters: (state) => {
            state.filters = {};
        },
        clearSelectedFile: (state) => {
            state.selectedFile = null;
        },
        setUploadProgress: (state, action: PayloadAction<number>) => {
            state.uploadProgress = action.payload;
        }
    },
    extraReducers: (builder) => {
        // Upload file
        builder
            .addCase(uploadCensusFile.pending, (state) => {
                state.uploading = true;
                state.error = null;
                state.uploadProgress = 0;
            })
            .addCase(uploadCensusFile.fulfilled, (state, action) => {
                state.uploading = false;
                state.uploadProgress = 100;
                if (action.payload.success && action.payload.file_info) {
                    state.files.unshift(action.payload.file_info);
                }
            })
            .addCase(uploadCensusFile.rejected, (state, action) => {
                state.uploading = false;
                state.uploadProgress = 0;
                state.error = action.payload as string;
            });

        // Fetch files
        builder
            .addCase(fetchCensusFiles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCensusFiles.fulfilled, (state, action) => {
                state.loading = false;
                state.files = action.payload.files;
            })
            .addCase(fetchCensusFiles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch file metadata
        builder
            .addCase(fetchFileMetadata.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFileMetadata.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedFile = action.payload;
            })
            .addCase(fetchFileMetadata.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Download file
        builder
            .addCase(downloadCensusFile.pending, (state) => {
                state.downloading = true;
                state.error = null;
            })
            .addCase(downloadCensusFile.fulfilled, (state) => {
                state.downloading = false;
            })
            .addCase(downloadCensusFile.rejected, (state, action) => {
                state.downloading = false;
                state.error = action.payload as string;
            });

        // Delete file
        builder
            .addCase(deleteCensusFile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteCensusFile.fulfilled, (state, action) => {
                state.loading = false;
                state.files = state.files.filter(file => file.id !== action.payload.fileId);
            })
            .addCase(deleteCensusFile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch plan summaries
        builder
            .addCase(fetchPlanSummaries.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPlanSummaries.fulfilled, (state, action) => {
                state.loading = false;
                state.planSummaries = action.payload.summaries;
            })
            .addCase(fetchPlanSummaries.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch statistics
        builder
            .addCase(fetchCensusStatistics.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCensusStatistics.fulfilled, (state, action) => {
                state.loading = false;
                state.statistics = action.payload;
            })
            .addCase(fetchCensusStatistics.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    }
});

export const { clearError, setFilters, clearFilters, clearSelectedFile, setUploadProgress } = censusSlice.actions;
export default censusSlice.reducer;